import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@/hooks/useTheme';
import ThemeToggle from './ThemeToggle';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { theme } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const links = [
    { label: 'Industries', to: '/industries' },
    { label: 'Case Studies', to: '/case-studies' },
    { label: 'How It Works', to: '/#how-it-works' },
    { label: 'Contact', to: '/contact' },
  ];

  const isDark = theme === 'dark';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 font-inter ${
        isDark
          ? scrolled
            ? 'bg-[#1E293B] backdrop-blur-xl border-b border-white/10'
            : 'bg-[#1E293B]/80 backdrop-blur-md border-b border-white/8'
          : scrolled
            ? 'bg-white/95 backdrop-blur-xl border-b border-[#E2E8F0]'
            : 'bg-white/70 backdrop-blur-md border-b border-[#E2E8F0]/60'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center cursor-pointer">
          <div className="w-[90px] h-[90px] flex items-center justify-center">
            <img
              src="https://public.readdy.ai/ai/img_res/cec110ae-2ba1-4571-aeb1-a9f5a3600a20.png"
              alt="PureHomeScale"
              className="w-full h-full object-contain"
            />
          </div>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap font-inter ${
                location.pathname === l.to
                  ? isDark ? 'text-white' : 'text-[#0F172A]'
                  : isDark ? 'text-white/60 hover:text-white' : 'text-[#334155]/60 hover:text-[#0F172A]'
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        {/* CTA + Theme Toggle */}
        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle />
          <Link
            to="/#book-demo"
            className={`font-semibold text-sm px-5 py-2.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap font-inter ${
              isDark
                ? 'bg-white text-[#0F172A] hover:bg-white/90'
                : 'bg-[#0F172A] text-white hover:bg-[#1e293b]'
            }`}
          >
            Book Demo
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${isDark ? 'text-white' : 'text-[#0F172A]'}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className={`md:hidden px-6 py-6 flex flex-col gap-5 ${isDark ? 'bg-[#0F172A]/98 border-white/10' : 'bg-white/98 border-[#E2E8F0]'} backdrop-blur-xl border-t`}>
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setMenuOpen(false)}
              className={`text-base font-medium cursor-pointer font-inter ${isDark ? 'text-white/70 hover:text-white' : 'text-[#334155] hover:text-[#0F172A]'}`}
            >
              {l.label}
            </Link>
          ))}
          <div className="flex items-center gap-3 pt-2">
            <ThemeToggle />
            <Link
              to="/#book-demo"
              onClick={() => setMenuOpen(false)}
              className={`font-semibold text-sm px-5 py-3 rounded-full text-center cursor-pointer whitespace-nowrap font-inter flex-1 ${
                isDark
                  ? 'bg-white text-[#0F172A]'
                  : 'bg-[#0F172A] text-white'
              }`}
            >
              Book Demo
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}